import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import PortalContainerPage from '../../components/PortalContainerPage/PortalContainerPage'
import PageTitle from '../../components/PageTitle/PageTitle'
import { useMyContextLoading } from '../../components/LoadingContextProvider/LoadingContextProvider'
import { showSnackBar } from '../../components/Snackbar/Snackbar'
import { getPortalActivity } from '../../features/portal/portal'

const PortalActivity = () => {
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()
  const [data, setData] = useState(null)
  const [activeTab, setActiveTab] = useState('timeline')

  useEffect(() => {
    const fetchData = async () => {
      triggerLoading(true)
      try {
        const result = await dispatch(getPortalActivity())
        if (result?.error) {
          showSnackBar('error', result?.payload || 'Failed to load activity')
        } else if (result?.payload?.data) {
          setData(result.payload.data)
        }
      } catch (error) {
        showSnackBar('error', 'An unexpected error occurred')
      }
      triggerLoading(false)
    }
    fetchData()
  }, [dispatch, triggerLoading])

  return (
    <PortalContainerPage>
      <PageTitle title="Activity" />

      {/* Tab switcher */}
      <div className="flex gap-0 mb-6 border-b border-divider">
        <button
          onClick={() => setActiveTab('timeline')}
          className={`py-3 px-4 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'timeline'
              ? 'border-primary-main text-primary-main'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          Document Timeline
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`py-3 px-4 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'history'
              ? 'border-primary-main text-primary-main'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          Recent History
        </button>
      </div>

      {/* Document Timeline */}
      {activeTab === 'timeline' && (
        <>
          {data?.documentTimeline?.length > 0 ? (
            data.documentTimeline.map((doc) => (
              <div key={doc._id} className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none mb-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-semibold text-md text-text-primary">{doc.name || 'Document'}</p>
                    <p className="text-[13px] text-text-secondary">
                      {doc.type?.label || 'Document'} | Created {new Date(doc.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>

                {/* Pizza Tracker Progress Bar */}
                <div className="flex items-center gap-0 w-full">
                  {doc.stages.map((stage, index) => (
                    <div key={stage.stage} className="flex-1 flex flex-col items-center relative">
                      {/* Connector line */}
                      {index > 0 && (
                        <div className={`absolute top-3 right-1/2 w-full h-0.5 ${stage.completed ? 'bg-success-main' : 'bg-divider'}`} />
                      )}
                      {/* Circle */}
                      <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        stage.completed
                          ? 'bg-success-main text-white'
                          : 'bg-paper border-2 border-divider text-text-secondary'
                      }`}>
                        {stage.completed ? '\u2713' : index + 1}
                      </div>
                      {/* Label */}
                      <p className={`text-[11px] mt-1.5 text-center ${stage.completed ? 'text-success-main font-medium' : 'text-text-secondary'}`}>
                        {stage.stage}
                      </p>
                      {/* Date */}
                      {stage.date && (
                        <p className="text-[10px] text-text-secondary">
                          {new Date(stage.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="py-10 text-center text-text-secondary text-sm">
              No document activity to show
            </div>
          )}
        </>
      )}

      {/* Recent History */}
      {activeTab === 'history' && (
        <div className="space-y-0">
          {data?.recentHistory?.length > 0 ? (
            data.recentHistory.map((entry) => (
              <div key={entry._id} className="flex items-start gap-3 py-3 border-b border-divider last:border-0">
                {entry.type && (
                  <span
                    className="text-[11px] font-medium px-2 py-0.5 rounded whitespace-nowrap mt-0.5"
                    style={{ backgroundColor: entry.type?.bgColor || '#E4E4E7', color: entry.type?.textColor || '#18181B' }}
                  >
                    {entry.type?.label || 'General'}
                  </span>
                )}
                <div className="flex-1">
                  <p className="text-sm text-text-primary">{entry.message}</p>
                  <p className="text-[12px] text-text-secondary mt-0.5">
                    {new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-text-secondary text-sm py-4 text-center">No recent history</p>
          )}
        </div>
      )}
    </PortalContainerPage>
  )
}

export default PortalActivity
